<template>
  <v-container v-if="$can('browse', 'ServiceTaskType')">
    <v-row
      no-gutters
      align="center"
      class="mb-6"
    >
      <v-col>
        <div class="headline">
          Task Types
        </div>
      </v-col>
      <v-col
        cols="auto"
      >
        <v-btn
          v-if="$can('create', 'ServiceTaskType')"
          color="primary"
          class="ma-0"
          @click="openServiceTaskTypeEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>

    <v-simple-table class="elevation-5">
      <thead class="v-data-table-header">
        <tr class="text-left">
          <th v-if="$can('edit', 'ServiceTaskType')" />
          <th>Order</th>
          <th>Title</th>
          <th class="text-right">
            Checklist
          </th>
          <th class="text-right">
            Actions
          </th>
        </tr>
      </thead>
      <draggable
        v-if="!serviceTaskTypes.loading && serviceTaskTypes.data.length"
        v-model="serviceTaskTypes.data"
        tag="tbody"
        handle=".sortHandle"
        element="tr"
        @change="onServiceTaskTypeMoved"
      >
        <service-task-type-row
          v-for="serviceTaskType in serviceTaskTypes.data"
          :key="serviceTaskType.id"
          :service-task-type="serviceTaskType"
          @editServiceTaskType="openServiceTaskTypeEditDialog"
          @deleteServiceTaskType="openServiceTaskTypeDeleteDialog"
        />
      </draggable>
    </v-simple-table>
    <v-row
      v-if="serviceTaskTypes.loading"
      class="justify-center align-center pa-6"
    >
      <v-col
        class="pa-6 shrink"
      >
        <v-progress-circular
          :size="50"
          :width="4"
          indeterminate
        />
      </v-col>
    </v-row>
    <v-row
      v-else-if="!serviceTaskTypes.data.length"

      class="text-center py-2"
    >
      <v-col cols="16">
        There are no service task types at the moment.
      </v-col>
      <v-col
        cols="16"
        class="mt-2"
      >
        <v-btn
          v-if="$can('create', 'ServiceTaskType')"
          color="primary"
          class="ma-0"
          small
          @click="openServiceTaskTypeEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>

    <service-task-type-edit-dialog
      :visible.sync="serviceTaskTypeEditDialog"
      :service-task-type="serviceTaskTypeDirty"
      @serviceTaskType:created="serviceTaskTypeCreated"
      @serviceTaskType:updated="serviceTaskTypeUpdated"
      @serviceTaskType:deleted="serviceTaskTypeDeleted"
    />

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this Service Task Type?"
      @onConfirm="deleteServiceTaskType"
    />
  </v-container>
</template>

<script>
import draggable from 'vuedraggable'
import ServiceTaskType from '@/models/ServiceTaskType'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ServiceTaskTypeEditDialog from '@/components/service/serviceTaskTypes/ServiceTaskTypeEditDialog'
import ServiceTaskTypeRow from '@/components/service/serviceTaskTypes/ServiceTaskTypeRow'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'

export default {
    name: 'ServiceTaskTypesIndex',
    components: {
      draggable,
      ConfirmDeleteDialog,
      ServiceTaskTypeEditDialog,
      ServiceTaskTypeRow
    },
    mixins: [deleteConfirmationMixin],
    data() {
      return {
        serviceTaskTypes: {
          data: [],
          loading: false,
          meta: {}
        },
        sorting: {
          order_by: 'order',
          order: 'asc'
        },
        serviceTaskTypeDirty: undefined,
        serviceTaskTypeEditDialog: false
      }
    },
    created() {
      this.fetchServiceTaskTypes()
    },
    methods: {
      async fetchServiceTaskTypes() {
        this.serviceTaskTypes.loading = true

        let queryParams = {
          ...this.sorting
        }

        try {
          this.serviceTaskTypes.data = await ServiceTaskType.api.fetch(queryParams)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.serviceTaskTypes.loading = false
        }
      },
      serviceTaskTypeCreated(serviceTaskType) {
        this.serviceTaskTypes.data.push({ ...serviceTaskType, new: true })
      },
      serviceTaskTypeUpdated(serviceTaskType) {
        const index = this.serviceTaskTypes.data.findIndex(stt => stt.id === serviceTaskType.id)
        this.serviceTaskTypes.data.splice(index, 1, serviceTaskType)
      },
      serviceTaskTypeDeleted(serviceTaskType) {
        this.fetchServiceTaskTypes()
      },
      openServiceTaskTypeEditDialog(serviceTaskType = undefined) {
        this.serviceTaskTypeDirty = serviceTaskType
        this.serviceTaskTypeEditDialog = true
      },
      openServiceTaskTypeDeleteDialog(serviceTaskType) {
        this.serviceTaskTypeDirty = serviceTaskType
        this.toggleConfirmationDialog(true)
      },
      /**
       * Creates array of updated items with new order values
       *
       * @param evt
       */
      onServiceTaskTypeMoved(evt) {
        const startIndex = evt.moved.oldIndex < evt.moved.newIndex ? evt.moved.oldIndex : evt.moved.newIndex
        const endIndex = evt.moved.oldIndex > evt.moved.newIndex ? evt.moved.oldIndex : evt.moved.newIndex
        let movedItems = []

        for (let i = startIndex; i <= endIndex; i++) {
          let movedItem = {
            id: this.serviceTaskTypes.data[i].id,
            order: i + 1
          }
          movedItems.push(movedItem)
        }

        this.updateOrder(movedItems)
      },
      async updateOrder(movedItemsIds) {
        this.serviceTaskTypes.loading = true
        const postData = {
          id_order: movedItemsIds
        }

        try {
          this.serviceTaskTypes.data = await ServiceTaskType.api.updateOrder(postData)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.serviceTaskTypes.loading = false
        }
      },
      async deleteServiceTaskType() {
        this.deleting = true

        try {
          await ServiceTaskType.api.destroy(this.serviceTaskTypeDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Service task type deleted',
            'color': 'warning'
          })
          this.fetchServiceTaskTypes()
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.deleteConfirmationDialogVisible = false
          this.deleting = false
        }
      }
    }
  }
</script>
